import { type ChangeEvent, useCallback, useMemo } from 'react';
import type { Lens } from '@hookform/lenses';
import { useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod/v4';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import type { InputDropdownOption } from '@interxfi/design-system/components/input';
import { type I18nNamespaces, type TFunction, useTranslation } from '@interxfi/translation';
import { IxcRegistrationV1UsState } from '@interxfi/types/generated/api';
import { sortByKey } from '@interxfi/utilities/sort';
import { AppMultiSelectInputWithLens } from '../../../../../../components/form/app-multi-select-input';
import { AppSelectInputWithLens } from '../../../../../../components/form/app-select-input';
import { ALL_US_STATES_AND_DC, ALL_US_STATES_AND_TERRITORIES } from '../../../controls/utils';

export const stateAndTerritoriesFormSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('none'),
  }),
  z.object({
    kind: z.literal('all'),
  }),
  z.object({
    kind: z.literal('allStatesPlusDc'),
  }),
  z.object({
    kind: z.literal('manual'),
    states: z.array(z.enum(IxcRegistrationV1UsState)),
  }),
]);

export type StateAndTerritoriesFormValues = z.infer<typeof stateAndTerritoriesFormSchema>;

const initialValuesByType = {
  none: { kind: 'none' },
  all: { kind: 'all' },
  allStatesPlusDc: { kind: 'allStatesPlusDc' },
  manual: { kind: 'manual', states: [] },
} as const satisfies Record<StateAndTerritoriesFormValues['kind'], StateAndTerritoriesFormValues>;

export function getInitialFormValuesForRegistrationStatesAndTerritories(
  states: IxcRegistrationV1UsState[] | undefined,
): StateAndTerritoriesFormValues {
  if (!states) {
    return { kind: 'none' } as const;
  }

  if (states.length === ALL_US_STATES_AND_TERRITORIES.length && states.every((s) => ALL_US_STATES_AND_TERRITORIES.includes(s))) {
    return { kind: 'all' } as const;
  }

  if (states.length === ALL_US_STATES_AND_DC.length && states.every((s) => ALL_US_STATES_AND_DC.includes(s))) {
    return { kind: 'allStatesPlusDc' } as const;
  }

  if (states.length > 0) {
    return {
      kind: 'manual',
      states,
    } as const;
  }

  return {
    kind: 'none',
  } as const;
}

export function formValuesToStatesAndTerritories(values: StateAndTerritoriesFormValues): IxcRegistrationV1UsState[] | undefined {
  switch (values.kind) {
    case 'all':
      return ALL_US_STATES_AND_TERRITORIES;
    case 'allStatesPlusDc':
      return ALL_US_STATES_AND_DC;
    case 'manual':
      return values.states.length > 0 ? values.states : undefined;
    default:
      return undefined;
  }
}

const kindOptions: InputDropdownOption<StateAndTerritoriesFormValues['kind']>[] = [
  {
    value: 'all',
    label: 'All 53 U.S. States & Territories',
  },
  {
    value: 'allStatesPlusDc',
    label: 'All 50 U.S. States + D.C.',
  },
  {
    value: 'manual',
    label: 'Manually Select',
  },
];

function getStateOptions(t: TFunction): InputDropdownOption<IxcRegistrationV1UsState>[] {
  return sortByKey(
    ALL_US_STATES_AND_TERRITORIES.map((val) => ({
      label: t(`ixcRegistrationV1:enum.IxcRegistrationV1UsState.${val}`),
      pillLabel: val,
      value: val,
    })),
    (x) => x.label,
  );
}

interface StateAndTerritoriesFormProps {
  lens: Lens<StateAndTerritoriesFormValues>;
}

const i18nNamespaces: I18nNamespaces[] = ['ixcRegistrationV1'];

export function StateAndTerritoriesForm({ lens }: StateAndTerritoriesFormProps) {
  const { t } = useTranslation(i18nNamespaces);
  const stateOptions = useMemo(() => getStateOptions(t), [t]);
  const { setValue } = useFormContext();
  const { name } = lens.interop();
  const selectedKind = useWatch(lens.focus('kind').interop());
  const handleChangeKind = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const defaultValue = initialValuesByType[e.target.value as StateAndTerritoriesFormValues['kind']];

      if (defaultValue) {
        setValue(name, defaultValue, { shouldValidate: true, shouldDirty: true });
      }
    },
    [setValue, name],
  );

  return (
    <VerticalStack gap={2}>
      <AppSelectInputWithLens label="State & Territory Registrations" lens={lens.focus('kind')} onChange={handleChangeKind} options={kindOptions} />

      {selectedKind === 'manual' ? (
        <AppMultiSelectInputWithLens
          label="Choose States & Territories"
          lens={lens.focus('states')}
          options={stateOptions}
          selectAllLabel="Select All"
        />
      ) : null}
    </VerticalStack>
  );
}
